import type { ReactNode } from 'react';
import { useGetUserBasicInfo } from '@/generated/hooks/agora/v1/user';
import { useGetAutomationSchedule } from '@/generated/hooks/grimoire/v1/api';
import {
  useRetrieveWorkspaceBookConnection,
  useRetrieveWorkspaceBooks,
  useRetrieveWorkspaceTriggerInstances,
} from '@/generated/hooks/bdk/v2/services/library';
import {
  V1AutomationStage,
  V1InvocationSource,
  type V1Run,
} from '@/generated/hooks/jeeves/models';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import {
  FilterBuilder,
  formatDisplayName,
  parseResourceName,
} from '@/shared/utils';
import { isEmailInvocation } from '@/shared/utils/run-utils';
import { convertToDataUrl } from '@/shared/utils/image-utils';
import { generateMailboxAddress } from '@/modules/triggers/utils/email-utils';
import {
  convertExpressionToFilters,
  parseEmailTriggerConfiguration,
} from '@/modules/triggers/components/TriggerTypeSelector/utils';
import i18n from '@/shared/i18n';
import { SchedulePreview } from './schedules/SchedulePreview';
import { parseScheduleConfig } from './schedules/utils';
import {
  EmailTriggerPopover,
  WebhookTriggerPopover,
} from './RunTriggerPopovers';

/**
 * The subline shown under a run's trigger badge, plus the detail revealed when
 * the badge is hovered.
 */
export interface IRunTriggerDetail {
  /** Subline text naming the specific trigger */
  label?: string;
  /** Hover card content, when there is more to show */
  popover?: ReactNode;
  /** Whether the popover needs the wider schedule-preview layout */
  isWidePopover?: boolean;
}

/**
 * Resolves what to show for a run's trigger: the user for manual and API runs,
 * the schedule summary, the mailbox address for email triggers, and the
 * integration, connection and event for webhook or poll triggers.
 *
 * @param data - The run to describe
 * @returns The subline and hover detail for the run's trigger
 */
export const useRunTriggerDetail = (data?: V1Run): IRunTriggerDetail => {
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const invocationDetails = data?.invocation_details;
  const source = invocationDetails?.invocation_source;
  const userId = invocationDetails?.user_id;
  const triggerId = invocationDetails?.trigger_id;
  const { automation: automationId } = parseResourceName(data?.name);

  const isUserRun =
    source === V1InvocationSource.INVOCATION_SOURCE_MANUAL ||
    source === V1InvocationSource.INVOCATION_SOURCE_PAT;
  const isScheduleRun =
    source === V1InvocationSource.INVOCATION_SOURCE_SCHEDULE;
  const isTriggerRun =
    source === V1InvocationSource.INVOCATION_SOURCE_TRIGGER &&
    Boolean(automationId && triggerId);
  const isEmail = isEmailInvocation(invocationDetails);
  const inWorkspace = Boolean(organizationId && workspaceId);

  const { data: userData, isError: isUserError } = useGetUserBasicInfo(
    organizationId ?? '',
    userId ?? '',
    {
      query: {
        enabled: Boolean(organizationId && userId && isUserRun),
        retry: false,
      },
    },
  );

  const { data: scheduleData, isError: isScheduleError } =
    useGetAutomationSchedule(
      organizationId ?? '',
      workspaceId ?? '',
      automationId ?? '',
      {
        query: {
          enabled: Boolean(inWorkspace && automationId && isScheduleRun),
          retry: false,
        },
      },
    );

  const { data: instancesData } = useRetrieveWorkspaceTriggerInstances(
    organizationId ?? '',
    workspaceId ?? '',
    {
      filter: FilterBuilder.create()
        .has('automation_ids', automationId ?? '')
        .build(),
    },
    {
      query: {
        enabled: Boolean(inWorkspace && automationId && isTriggerRun),
        retry: false,
      },
    },
  );
  const instance = instancesData?.data?.trigger_instances?.find(
    ({ id }) => id === triggerId,
  );

  // Trigger IDs are book-prefixed ("github-wqsm2-trigger-…"), the same shape the
  // email check relies on, so the book is still identifiable when the instance
  // can't be resolved. Only used to match a real book, never shown raw.
  const bookName = instance?.book_name || triggerId?.split('-')[0];

  // Filter by name: the unfiltered list is one entry per book *version* and is
  // paginated, so an alphabetically-late book never appears in the first page.
  const { data: booksData } = useRetrieveWorkspaceBooks(
    organizationId ?? '',
    workspaceId ?? '',
    {
      page_size: 1,
      filter: FilterBuilder.create()
        .equals('name', bookName ?? '')
        .build(),
    },
    {
      query: {
        enabled: Boolean(inWorkspace && bookName && isTriggerRun && !isEmail),
        retry: false,
      },
    },
  );
  const book = booksData?.data?.books?.[0];

  const connectionId = instance?.connection_id;
  const { data: connectionData } = useRetrieveWorkspaceBookConnection(
    organizationId ?? '',
    workspaceId ?? '',
    connectionId ?? '',
    {
      query: {
        enabled: Boolean(
          inWorkspace && connectionId && isTriggerRun && !isEmail,
        ),
        retry: false,
      },
    },
  );

  if (isUserRun) {
    const name = userData?.data?.display_name || userData?.data?.email;
    if (isUserError || !name) return {};

    return {
      label: i18n.t('automation.run.invocationSource.triggeredBy', {
        ns: 'automation',
        name,
      }),
    };
  }

  if (isScheduleRun) {
    const schedule = scheduleData?.data;
    const summary = schedule?.summary;
    if (isScheduleError || !schedule || !summary) return {};

    return {
      label: summary,
      isWidePopover: true,
      popover: (
        <SchedulePreview
          scheduleConfig={parseScheduleConfig(schedule)}
          summary={summary}
          orientation="horizontal"
          showViewMore={false}
        />
      ),
    };
  }

  if (!isTriggerRun || !automationId) return {};

  if (isEmail) {
    // An email trigger's address is well-known from the automation, so it stays
    // resolvable even when the instance lookup returns nothing.
    const address =
      parseEmailTriggerConfiguration(instance?.configuration) ||
      generateMailboxAddress(
        automationId,
        data?.stage === V1AutomationStage.AUTOMATION_STAGE_DRAFT,
      );

    return {
      label: address,
      popover: (
        <EmailTriggerPopover
          address={address}
          filters={convertExpressionToFilters(instance?.filter_expression)}
        />
      ),
    };
  }

  const integrationName =
    book?.display_name ||
    (instance?.book_name
      ? formatDisplayName(instance.book_name)
      : instance?.name);
  const connectionName = connectionData?.data?.book_connection?.connection_name;
  // The books list returns an empty triggers[], so the type is formatted from the
  // instance's trigger id ("pull_request_labeled" → "Pull request labeled").
  const triggerType = instance?.trigger_id
    ? formatDisplayName(instance.trigger_id)
    : undefined;

  // Segments the instance can't supply are dropped rather than left blank.
  const label = [integrationName, connectionName, triggerType]
    .filter(Boolean)
    .join(' · ');
  if (!label) return {};

  return {
    label,
    popover: (
      <WebhookTriggerPopover
        app={integrationName}
        appIcon={convertToDataUrl(book?.icon)}
        connection={connectionName}
        event={triggerType}
      />
    ),
  };
};
